import cx from 'classnames';
import {castArray, filter, isFunction} from 'lodash';
import {ReactElement, cloneElement, useCallback, useMemo, useState} from 'react';

type Spacing = 'top' | 'bottom' ;

type FormControlChildrenProps = {
    disabled: boolean;
    onFocus: () => void;
    onBlur: (e?: React.FocusEvent) => void;
}

export type FormControlProps = {
    value: any;
    label?: string;
    description?: string | string[];
    className?: string;
    validate?: boolean;
    required?: boolean;
    disabled?: boolean;
    skipErrorMessage?: boolean;
    styled?: boolean;
    childProps?: any;
    hasError?: boolean;
    errorMessage?: string;
    dirty?: boolean;
    active?: boolean;
    fontLarge?: boolean;
    spacing?: Spacing;
    onBlur?: (e: React.FocusEvent) => void
    children: (props: FormControlChildrenProps) => ReactElement;
};

export const FormControl: React.FC<FormControlProps> = ({
    value, hasError, dirty, className, label, description, required, disabled, errorMessage,
    skipErrorMessage, styled = true, validate = true, active, childProps, fontLarge, spacing, children, onBlur
}: FormControlProps) => {
    const [focused, setFocused] = useState(false);

    const invalid = useMemo(() => (!focused || !dirty) && hasError, [focused, dirty, hasError]);

    const onFocusHandler = useCallback(() => setFocused(true), []);

    const onBlurHandler = useCallback((e: React.FocusEvent) => {
        onBlur?.(e);
        setFocused(false);
    }, [onBlur]);

    const mergedChildProps = useMemo(() => ({
        ...(childProps && childProps),
        disabled, onFocus: onFocusHandler, onBlur: onBlurHandler,
    }), [childProps, disabled, onFocusHandler, onBlurHandler]);

    return (
        <div
            className={cx(className, {
                'vs-form-control': styled,
                active: focused || value || active,
                'tw-mt-controls': spacing === 'top',
                'tw-mb-controls': spacing === 'bottom'
            })}
        >
            {label &&
                <label className={cx('label', {'font-large': fontLarge})}>
                    {label}
                    {required && <span className='required'>{'*'}</span>}
                </label>}
            <div className={cx('field', {
                'stateful-field': styled,
                filled: !invalid && value || !validate,
                // 'valid': !focus && !hasError && (dirty || value), TODO Fix valid color
                invalid,
                focused,
                disabled,
            })}
            >
                {isFunction(children) ? children(mergedChildProps) : cloneElement(children, mergedChildProps)}
            </div>
            {castArray(filter(description)).map((d, i) => <div key={i} className='tw-text-default-dimmed tw-text-sm'>{d}</div>)}
            {invalid && errorMessage && !skipErrorMessage && <div className='message error'>{errorMessage}</div>}
        </div>
    );
};
